import { useEffect, useState } from "react";
import { TextInput, CustomModal, FormError, showToast } from "../shared";
import { extractBackendFieldErrors } from "../../utils/utils";
import { PROJECT_ROLE_OPTIONS } from "../../constants/roles";
import { useUpdateProjectMember } from "../../lib/react-query/projects";

const EditProjectMemberModal = ({ isOpen, onClose, member, projectId }) => {
  const { mutateAsync: updateMember, isLoading: updating } = useUpdateProjectMember();
  const [state, setState] = useState({ loading: false, error: null, fieldErrors: {} });
  const [form, setForm] = useState({
    title: "",
    role: "COLLABORATOR",
  });

  useEffect(() => {
    if (isOpen && member) {
      setForm({
        title: member?.title || "",
        role: member?.role || "COLLABORATOR",
      });
      setState({ loading: false, error: null, fieldErrors: {} });
    }
    if (!isOpen) {
      setState({ loading: false, error: null, fieldErrors: {} });
    }
  }, [isOpen, member]);

  const handleUpdate = async () => {
    if (!member?.user?.id) {
      setState((p) => ({ ...p, error: "Member ID is missing" }));
      return;
    }

    if (!projectId) {
      setState((p) => ({ ...p, error: "Project ID is missing" }));
      return;
    }

    try {
      setState({ loading: true, error: null, fieldErrors: {} });
      const userId = member?.user?.id;
      const payload = {
        title: form.title,
        role: form.role,
      };
      await updateMember({ projectId, userId, data: payload });
      showToast("Member updated successfully", "success", { position: "top-center" });
      onClose?.();
    } catch (err) {
      const { fieldErrors: backendErrors, generalError } = extractBackendFieldErrors(err);
      setState({
        loading: false,
        error: generalError,
        fieldErrors: backendErrors,
      });
      return;
    }
  };

  return (
    <CustomModal
      title="Edit Project Member"
      open={isOpen}
      close={onClose}
      primaryButtonText="Save Changes"
      primaryButtonClick={handleUpdate}
      primaryButtonLoading={state.loading || updating}
      secondaryButtonText="Cancel"
      secondaryButtonClick={onClose}
    >
      <div className="flex flex-col gap-3">
        <TextInput
          label="Title"
          type="text"
          name="title"
          value={form.title}
          onChange={(e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }))}
          placeholder="e.g. Project Manager"
        />
        <div className="space-y-3">
          <span id="project-member-role-label" className="text-sm font-medium text-darkgrey">Role</span>
          <div role="group" aria-labelledby="project-member-role-label" className="space-y-2">
            {PROJECT_ROLE_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`w-full text-left px-3 py-3 rounded-lg border transition-colors ${
                  form.role === option.id
                    ? "border-primary bg-primary/5"
                    : "border-surface-border hover:border-surface-border"
                }`}
                onClick={() => setForm((p) => ({ ...p, role: option.id }))}
              >
                <div className="font-medium text-darkgrey">{option.name}</div>
                <div className="text-xs text-grey mt-0.5">
                  {option.id === "MANAGER" && "Can manage members, edit project settings, and create tasks"}
                  {option.id === "COLLABORATOR" && "Can work on tasks assigned to them"}
                  {option.id === "EXTERNAL_STAKEHOLDER" && "Read-only access for client contacts"}
                </div>
              </button>
            ))}
          </div>
        </div>
        <FormError message={state.error} />
      </div>
    </CustomModal>
  );
};

export default EditProjectMemberModal;
